import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Text, Image, HStack, IconButton, Box, VStack } from '@chakra-ui/react';

import AppRoutes from 'config/AppRoutes';
import PageLayout from 'ui/layout/PageLayout';

import Button from 'ui/molecules/Button';
import { useState, useCallback } from 'react';
import InputNumber from 'ui/molecules/InputNumber';
import { useDispatch, useSelector } from 'react-redux';
import { updateBill } from 'store/answers/answersSlice';
import {
  AddressPostcodeSelector,
  BillSelector,
  BusinessChoiceSelector,
  PanelSelector,
} from 'store/selectors';
import { ImArrowRight2, ImMinus, ImPlus } from 'react-icons/im';
import RoundSlider from 'ui/molecules/RoundSlider';
import PanelIndicator from 'components/PanelIndicator';
import useGetRegionPrice from 'api/useGetRegionPrice';
import {
  calculateConsumptionBaseOnBill,
  getEstimatedPanelsForConsumption,
} from 'utils/calculation/calculation';
import { formatNumber } from 'utils/formatNumber';
import useAsset, { AssetType } from 'utils/hooks/useAsset';
import { SMEContactReason } from 'pages/PageSMEContact/PageSMEContact';
import useStepNavigation from 'utils/hooks/useStepNavigation';
import usePanelList from '../PagePanel/hooks/usePanelList';
import useBillConfig from './hooks/useBillConfig';

const PageBillInfo = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userBillAmount = useSelector(BillSelector);
  const panelList = usePanelList();
  const panelId = useSelector(PanelSelector);
  const addressPostCode = useSelector(AddressPostcodeSelector);
  const { navigateToNextStep } = useStepNavigation(AppRoutes.CONSUMPTION);
  const dispatch = useDispatch();
  const {
    default: defaultBillAmount,
    max,
    min,
    buttonStep: step,
    sliderStep,
  } = useBillConfig();

  const [currentValue, setCurrentValue] = useState<number>(
    userBillAmount || defaultBillAmount
  );
  const regionPrice = useGetRegionPrice(addressPostCode);
  const calculatedConsumption = calculateConsumptionBaseOnBill(
    currentValue,
    regionPrice
  );
  const businessChoice = useSelector(BusinessChoiceSelector);
  const shouldGoToSMEContactPage =
    businessChoice === 'b2b' && currentValue === max;
  const continueLabel = shouldGoToSMEContactPage
    ? t('BILL_INFO_b2b.max.continue', {
        amount: formatNumber(max),
      })
    : t('common.continue');

  // --------------------- methods ------------------------

  const changeValue = useCallback(
    (value: number, isEnd: boolean = false) => {
      const val = value > max ? max : value;
      setCurrentValue(val);
      if (isEnd) dispatch(updateBill(val));
    },
    [dispatch, max]
  );

  const handleContinue = () => {
    dispatch(updateBill(currentValue)); // TODO: not sure this is necessary as the buttons are disabled until we have a value

    if (shouldGoToSMEContactPage) {
      navigate(
        `${AppRoutes.SME_CONTACT}?contactReason=${SMEContactReason.EUR}`
      );
      return;
    }
    navigateToNextStep();
  };

  const handleSkip = () => {
    dispatch(updateBill(undefined));
    navigate(AppRoutes.BILL_INFO_DEFAULT);
  };

  const imagePos = Math.floor(((currentValue - min) / (max - min)) * 150);
  let imageId = `${imagePos}`;
  if (imagePos < 10) imageId = `00${imagePos}`;
  else if (imagePos < 100) imageId = `0${imagePos}`;

  const billImageUrl = useAsset(
    AssetType.VIDEO,
    `/04-prix/PIECES 1000${imageId}.jpg`
  );

  return (
    <PageLayout
      title={t('BILL_INFO_title')}
      buttonLabel={continueLabel}
      buttonCallback={handleContinue}
      button={
        shouldGoToSMEContactPage && (
          <Button
            variant="multilineWithArrow"
            maxW="300px"
            rightIcon={<ImArrowRight2 />}
            as={motion.button}
            initial={{ opacity: 0, translateX: -0, scale: 1.2 }}
            animate={{
              opacity: 1,
              scale: 1.0,
              translateX: 0,
              transition: { duration: 0.3, ease: 'anticipate' },
            }}
            exit={{ opacity: 0, translateX: 30, transition: { duration: 0.3 } }}
            onClick={handleContinue}
          >
            {t('BILL_INFO_b2b.max.continue', {
              amount: formatNumber(max),
            })}
          </Button>
        )
      }
      backButton
      backRoute={AppRoutes.CONSUMPTION}
    >
      <Box
        height={{ base: '150px', md: '200px' }}
        position="relative"
        top={{ md: '25px' }}
      >
        <Image
          marginTop={{ base: '-100px', md: '-80px' }}
          height={{ base: '300px', md: '300px' }}
          src={billImageUrl}
          alt="bill-selection"
        />
        <PanelIndicator
          amount={getEstimatedPanelsForConsumption(
            panelId,
            panelList,
            calculatedConsumption
          )}
          txt={t('common.panels.needed')}
          textPosition="left"
          top={{ base: '50px', md: '50px' }}
          right={{ base: '-20px', md: '-50px' }}
        />
      </Box>

      <VStack w="full">
        <VStack gap="0" position="relative" pb="15px">
          <InputNumber
            min={min}
            max={max}
            value={currentValue}
            onChange={(val) => changeValue(val, true)}
            mb="-12px"
          />
          <Text
            position="absolute"
            top="75px"
            color="primaryColor"
            bgColor="white"
            px="10px"
            fontSize={24}
            fontFamily="Primary Regular"
          >
            {t('BILL_INFO_euro')}
          </Text>
        </VStack>

        <HStack w="full">
          <IconButton
            aria-label="increase bill"
            color="primaryColor"
            colorScheme="whiteAlpha"
            icon={<ImMinus />}
            onClick={() => {
              if (currentValue > step) {
                changeValue(currentValue - step, true);
              }
            }}
          />
          <RoundSlider
            min={min}
            max={max}
            baseValue={currentValue}
            step={sliderStep}
            onChange={(val) => changeValue(val)}
            onChangeEnd={(val) => changeValue(val, true)}
          />

          <IconButton
            aria-label="increase bill"
            color="primaryColor"
            colorScheme="whiteAlpha"
            onClick={() => {
              changeValue(currentValue + step, true);
            }}
            icon={<ImPlus />}
          />
        </HStack>
        <Button
          variant="skip"
          width="full"
          onClick={handleSkip}
          fontSize={{ base: '15px' }}
        >
          {t('BILL_INFO_skip')}
        </Button>
      </VStack>
    </PageLayout>
  );
};

export default PageBillInfo;
